import { useState } from 'react';
import { Coffee, Sparkles, Check, ExternalLink } from 'lucide-react';

const PAYPAL_EMAIL = process.env.REACT_APP_PAYPAL_EMAIL || 'alyx1012002@yahoo.com';

const amounts = [
  { value: 3, label: '$3', emoji: 'One coffee' },
  { value: 5, label: '$5', emoji: 'A latte' },
  { value: 10, label: '$10', emoji: 'Coffee for two' },
];

export default function DonatePage() {
  const [selected, setSelected] = useState(null);
  const [custom, setCustom] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const getAmount = () => {
    if (useCustom) {
      const val = parseFloat(custom);
      return val > 0 ? val : 0;
    }
    return selected || 0;
  };

  const handleTip = () => {
    const amount = getAmount();
    if (amount <= 0) return;
    const returnUrl = `${window.location.origin}/payment/success`;
    const params = new URLSearchParams({
      business: PAYPAL_EMAIL,
      amount: amount.toFixed(2),
      currency_code: 'USD',
      no_recurring: '1',
      item_name: 'Retro Playground TV — Buy me a coffee',
      return: returnUrl,
      cancel_return: `${window.location.origin}/donate`,
    });
    window.open(`https://www.paypal.com/donate/?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="page-content bg-[#FDFBEC] min-h-screen">
      <div className="pattern-bg" />
      <div className="max-w-lg mx-auto px-6 md:px-8 py-16 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{
              backgroundColor: '#FFDE00',
              border: '2px solid #0A0A0A',
              boxShadow: '4px 4px 0 0 #0A0A0A',
            }}
          >
            <Coffee size={36} strokeWidth={2.2} className="text-[#0A0A0A]" />
          </div>
          <h1
            className="font-['Fredoka'] text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight leading-none mb-3"
            data-testid="donate-title"
          >
            Buy me a <span className="text-[#FF3860]">coffee</span>
          </h1>
          <p className="text-base text-[#4B5563] font-['Nunito'] max-w-sm mx-auto">
            Retro Playground TV is completely free — always. If you enjoy the show, a small tip
            keeps the lights on. Your kindness pays for hosting and site upkeep, nothing else.
          </p>
        </div>

        {/* Tip Card */}
        <div className="neo-card p-6 bg-white" data-testid="donation-card">
          <h2 className="font-['Fredoka'] text-lg font-bold text-[#0A0A0A] mb-4">Pick your cup ☕</h2>

          {/* Preset Amounts */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {amounts.map(a => (
              <button
                key={a.value}
                onClick={() => { setSelected(a.value); setUseCustom(false); }}
                className={`neo-card p-4 text-center transition-all ${
                  selected === a.value && !useCustom
                    ? 'ring-2 ring-[#FF3860] bg-[#FF3860]/5 translate-y-[-2px]'
                    : 'bg-white hover:bg-[#F4F4F5]'
                }`}
                data-testid={`donate-${a.value}`}
              >
                <span className="font-['Fredoka'] text-2xl font-black text-[#0A0A0A] block">{a.label}</span>
                <span className="text-xs text-[#4B5563] font-['Nunito']">{a.emoji}</span>
              </button>
            ))}
          </div>

          {/* Custom Amount */}
          <div className="mb-6">
            <button
              onClick={() => { setUseCustom(true); setSelected(null); }}
              className={`w-full text-left text-sm font-bold font-['Fredoka'] mb-2 ${useCustom ? 'text-[#FF3860]' : 'text-[#4B5563]'}`}
              data-testid="custom-amount-toggle"
            >
              Or pick your own amount
            </button>
            {useCustom && (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-['Fredoka'] font-bold text-[#0A0A0A]">$</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 border-2 border-[#0A0A0A] rounded-lg text-lg font-['IBM_Plex_Mono'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
                  data-testid="custom-amount-input"
                />
              </div>
            )}
          </div>

          {/* Coffee Button */}
          <button
            onClick={handleTip}
            disabled={getAmount() <= 0}
            className="neo-btn w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-[#FFDE00] text-[#0A0A0A] text-lg font-bold disabled:opacity-40"
            style={{ borderColor: '#0A0A0A' }}
            data-testid="donate-btn"
          >
            <Coffee size={20} strokeWidth={2.5} />
            {getAmount() > 0 ? `Buy me a $${getAmount()} coffee` : 'Pick a cup first'}
            {getAmount() > 0 && <ExternalLink size={16} strokeWidth={2.5} />}
          </button>

          {/* Notes */}
          <p className="text-xs text-center text-[#4B5563] font-['Nunito'] mt-4">
            Payments are processed securely by PayPal. You can pay with a PayPal account
            or a credit/debit card — no account required. One-time tip, no subscription.
          </p>
          <p className="text-xs text-center text-[#0A0A0A] font-['Nunito'] font-bold mt-2" data-testid="donate-tax-note">
            Donations are not tax-deductible and support site hosting and maintenance.
          </p>
        </div>

        {/* What Your Coffee Supports */}
        <div className="neo-card p-5 mt-6 bg-white" data-testid="donation-info">
          <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A] mb-3 flex items-center gap-2">
            <Sparkles size={16} strokeWidth={2.5} className="text-[#FFDE00]" /> What your coffee keeps running
          </h3>
          <ul className="space-y-2">
            {[
              'Hosting and server costs so the site stays up',
              'Time spent curating and organizing the show library',
              'Building calm parent tools and family-friendly features',
              'Everything stays free for everyone — this is purely optional',
            ].map(item => (
              <li key={item} className="flex items-start gap-2 text-sm text-[#4B5563] font-['Nunito']">
                <Check size={14} strokeWidth={3} className="text-[#39FF14] mt-0.5 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
